<!-- eslint-disable -->

<template>
  <div data-cy="sizing-menu-container">
    <Button
      aria-haspopup="true"
      aria-controls="overlay_menu"
      :disabled="isNewSizing"
      data-cy="sizing-menu-button"
      @click="toggle"
    >
      <icon :icon="['fas', 'ellipsis-v']" size="lg" />
    </Button>
    <Menu
      ref="menu"
      class="sizing-menu"
      :popup="true"
      :model="menuItems"
      :pt="{ action: { 'aria-hidden': false } }"
      data-cy="sizing-menu-dropdown"
    />
    <a
      ref="exportDownloadAnchor"
      style="display: none"
      data-cy="export-download-anchor"
    ></a>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable */
// @ts-nocheck

import { useRouter, type RouteRecordName } from "vue-router";
import type { MenuItem } from "primevue/menuitem";

import { computed, ref } from "vue";
import { injectStrict } from "@/utils/utils";

import { useEditPermissions } from "@/composables/auth/editPermissions";
import useUserInfo from "@/composables/auth/useUserInfo";
import {
  sizingFormKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { buildInfoKey } from "@/injectionKeys";
import exportSizing from "@/utils/exports/exportSizing";
import Menu from "primevue/menu";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import { useToast } from "primevue/usetoast";

const props = defineProps<{
  sizingId: string | null | undefined;
  routeName: RouteRecordName | null | undefined;
}>();

const router = useRouter();
const buildInfo = injectStrict(buildInfoKey);
const sizing = injectStrict(sizingKey);

const emit = defineEmits<{
  (e: "clone"): void;
  (e: "exportSizing"): void;
  (e: "exportSurveys"): void;
  (e: "delete"): void;
}>();

const sizingForm = injectStrict(sizingFormKey);

const user = useUserInfo();

const toaster = useToast();

const { isSizingReadonly } = useEditPermissions(sizingForm, null);

const isNewSizing = computed(() => props.sizingId === "new");

const hasDeviationSurvey = computed(() => {
  if (!sizing.value?.scenarios) {
    return false;
  }
  return sizing.value.scenarios.some(
    scenario => scenario.well_properties?.r_deviation_survey
  );
});

const menu = ref();
function toggle(event: MouseEvent): void {
  menu.value.toggle(event);
}

const exportDownloadAnchor = ref<HTMLAnchorElement>();
const menuItems = computed<MenuItem[]>(() => [
  {
    label: "Generate Report",
    icon: "fas fa-file-chart-line",
    class: "success",
    command: () =>
      router.push({
        name: "Reports",
        params: { sizingId: props.sizingId }
      })
  },
  {
    visible: () => !user.is_customer && sizing.value?.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore,
    label: "Create Quotes",
    icon: "fas fa-file-invoice-dollar",
    class: "success",
    command: () =>
      router.push({
        name: "SizingQuotes",
        params: { sizingId: props.sizingId }
      })
  },
  {
    label: "Clone Sizing",
    icon: "fas fa-clone",
    class: "info",
    visible: () => props.routeName !== "Reports",
    command: () => emit("clone")
  },
  {
    label: "Delete Sizing",
    icon: "fas fa-trash",
    class: "danger",
    visible: () => user.has_elevated_privileges && !isSizingReadonly.value,
    command: () => emit("delete")
  },

  {
    label: "Copy URL",
    icon: "fas fa-clipboard",
    class: "gray",
    command() {
      navigator.clipboard.writeText(window.location.href);
      toaster.add({
        severity: "info",
        life: 4000,
        detail: "Sizing URL Copied!"
      });
    }
  },
  {
    label: "Export JSON",
    icon: "fas fa-arrow-down-to-line",
    class: "gray",
    visible: !!user.is_admin,
    command() {
      if (buildInfo && sizing.value && exportDownloadAnchor.value) {
        const { filename, blob } = exportSizing(buildInfo, sizing.value);

        // stringify the JSON and fake it as a static file download
        const a = exportDownloadAnchor.value;
        a.href = blob;
        a.download = filename;
        a.click();
      }
    }
  },
    {
    label: "Export Deviation Survey",
    icon: "fas fa-arrow-down-to-line",
    class: "gray",
    visible: hasDeviationSurvey.value,
    command: () => emit("exportSurveys")
  },
  {
    label: "Export Sizing (XLSX)",
    icon: "fas fa-arrow-down-to-line",
    class: "gray",
    visible: !user.is_customer,
    command: () => emit("exportSizing")
  },

]);
</script>
